import type { User } from '@/@types/user'
import {
	FOLLOWER_LIST,
	FOLLOWING_LIST,
	FOLLOW_USER,
	UNFOLLOW_USER,
	USER_PROFILE,
} from '@/constants/api'
import { get, post } from '@/utils/request'
import type {
	InfiniteData,
	UseInfiniteQueryOptions,
	UseQueryOptions,
	UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import queryClient from '../queryClient'

export function getUserQueryOptions(username: string | undefined): UseQueryOptions<User> {
	return {
		queryKey: [USER_PROFILE, username],
		queryFn: async () => getUserProfile(username),
		staleTime: 1000 * 60 * 60 * 6,
	}
}

export const currentUserQueryOptions = getUserQueryOptions(void 0) as UseSuspenseQueryOptions<User>

export async function getUserProfile(username?: string) {
	return get(USER_PROFILE, {
		params: {
			username,
		},
	}).then((res) => res.data.user as User)
}

export async function followUser(username: string) {
	return post(FOLLOW_USER, {
		username,
	})
}

export async function unfollowUser(username: string) {
	return post(UNFOLLOW_USER, {
		username,
	})
}

interface QueryUserListPage {
	data: User[]
	loadMoreKey?: unknown
}
export function getFollowingListQueryOptions(
	username: string
): UseInfiniteQueryOptions<QueryUserListPage, unknown, InfiniteData<QueryUserListPage>> {
	return {
		queryKey: [FOLLOWING_LIST, username],
		queryFn: ({ pageParam }) =>
			post<QueryUserListPage>(FOLLOWING_LIST, {
				limit: 20,
				loadMoreKey: pageParam,
				username,
			}).then((res) => {
				const body = res.data

				for (const user of body.data) {
					queryClient.setQueryData(getUserQueryOptions(user.username).queryKey, user)
				}

				return body
			}),
		getNextPageParam: (lastPage: QueryUserListPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		initialData: {
			pages: [],
			pageParams: [],
		},
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

export function getFollowerListQueryOptions(
	username: string
): UseInfiniteQueryOptions<QueryUserListPage, unknown, InfiniteData<QueryUserListPage>> {
	return {
		queryKey: [FOLLOWER_LIST, username],
		queryFn: ({ pageParam }) =>
			post<QueryUserListPage>(FOLLOWER_LIST, {
				limit: 20,
				loadMoreKey: pageParam,
				username,
			}).then((res) => {
				const body = res.data

				// cache posts
				for (const user of body.data) {
					queryClient.setQueryData(getUserQueryOptions(user.username).queryKey, user)
				}

				return body
			}),
		getNextPageParam: (lastPage: QueryUserListPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		initialData: {
			pages: [],
			pageParams: [],
		},
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}
